import React, { useEffect, useState, useMemo } from "react";
import { Layout, message, Modal } from "antd";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../data/base";
import logo from "../assets/images/logo.png";
import styles from "../styles/Dashboard/Dashboard.module.css";
import { theme } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import {
    doc,
    collection,
    query,
    where,
    getDocs,
    getDoc
} from "firebase/firestore";
import { useDispatch, useSelector } from "react-redux";
import {
    setSidebarState,
    setSidebarBroken,
} from "../redux/sidebar";
import { fetchNotifications } from "../redux/notifications/actions";
import { useMediaQuery } from "react-responsive";
import { db } from "../data/base";
import { secureGetItem } from "../data/utils";
import { primaryShades, statusTokens, variantTokens } from "../theme/theme";

// Importazione delle icone di Material UI
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardSharpIcon from '@mui/icons-material/DashboardSharp';
import StorageIcon from '@mui/icons-material/Storage';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Importazione delle icone SVG personalizzate
import ARShadesLibraryIcon from '../assets/icon/ARShades Library icon.svg';
import ARShadesCataloguesIcon from '../assets/icon/ARShades Catalogues icon.svg';
import ARShadesSubscriptionsIcon from '../assets/icon/ARShades Subscriptions icon.svg';
import ARShadesOrdersIcon from '../assets/icon/ARShades Orders icon2.png';

// Importazione del componente NotificationsDropdown
import NotificationsDropdown from '../frameValidation/modal/NotificationsDropdown';

// Importazione del componente SupportModal
import SupportModal from './SupportModal';

const { Header, Content } = Layout;

// Funzione per convertire colore esadecimale in filtro CSS per SVG [[memory:2966829]]
const hexToFilter = (hex) => {
    // Se il colore non è una stringa, ritorna none
    if (typeof hex !== 'string') {
        return 'none';
    }

    // Rimuovi il # se presente
    hex = hex.replace('#', '');

    // Per primaryShades.primary400 (#6774b6) che è il colore delle icone inattive
    if (hex.toLowerCase() === '6774b6') {
        return 'brightness(0) saturate(100%) invert(44%) sepia(18%) saturate(1142%) hue-rotate(201deg) brightness(95%) contrast(86%)';
    }

    // Per il colore bianco delle icone attive (#ffffff)
    if (hex.toLowerCase() === 'ffffff') {
        return 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)';
    }

    // Per altri colori, prova un filtro generico per renderli più scuri
    return 'brightness(0) saturate(100%) invert(44%) sepia(18%) saturate(1142%) hue-rotate(201deg) brightness(95%) contrast(86%)';
};

// Componenti per le icone SVG personalizzate [[memory:2966829]]
const CustomSVGIcon = ({ src, alt, style, ...props }) => {
    // Applica il filtro solo se abbiamo un colore valido
    const filter = style?.color ? hexToFilter(style.color) : 'none';

    return (
        <img
            src={src}
            alt={alt}
            style={{
                width: style?.fontSize || '1.75rem',
                height: style?.fontSize || '1.75rem',
                filter: filter,
                transition: 'filter 0.3s ease',
                // Rimuovi il color dal style per evitare conflitti
                ...Object.fromEntries(Object.entries(style || {}).filter(([key]) => key !== 'color'))
            }}
            {...props}
        />
    );
};

const DashboardLayout = () => {

    const { token } = theme.useToken();
    const iconSize = "clamp(1.75rem, 2vw, 2rem)";
    const textActiveColor = token.colorTextWhite;
    const textColor = primaryShades.primary400;
    const backgroundColor = token.colorBgBase;
    const backgroundColorActive = variantTokens.accent;
    const headerTextColor = primaryShades.primary700;
    const badgeBackgroundColor = textColor;
    const badgeIconColor = token.colorTextWhite;
    const badgeBgColor = statusTokens.error;

    // Calcoliamo il colore hover partendo dal colore attivo
    // Leggero schiarimento del colore di sfondo attivo per l'hover
    const backgroundColorActiveHover = useMemo(() => {
        // Controlliamo se backgroundColorActive è un valore esadecimale
        if (backgroundColorActive.startsWith('#')) {
            // Convertiamo da HEX a RGB
            const r = parseInt(backgroundColorActive.slice(1, 3), 16);
            const g = parseInt(backgroundColorActive.slice(3, 5), 16);
            const b = parseInt(backgroundColorActive.slice(5, 7), 16);

            // Schiarisci leggermente i valori RGB (max 255)
            const lightenFactor = 20;
            const newR = Math.min(r + lightenFactor, 255);
            const newG = Math.min(g + lightenFactor, 255);
            const newB = Math.min(b + lightenFactor, 255);

            // Riconverti in HEX
            return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
        }

        // Se non è un valore esadecimale, restituisci un valore di fallback 
        return 'rgba(75, 85, 130, 0.9)';
    }, [backgroundColorActive]);

    const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
    const [isSupportModalVisible, setIsSupportModalVisible] = useState(false);
    const [openKeys, setOpenKeys] = useState([]);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [mappedRole, setMappedRole] = useState("");

    // Selettori Redux per le notifiche
    const frameValidationNotifCount = useSelector(state => state.notifications.frameValidationCount);
    const ordersNotifCount = useSelector(state => state.notifications.ordersCount);

    const navigate = useNavigate();
    const location = useLocation();

    // Funzione per mappare il ruolo dal server (identica a GeneralInfoSection.js)
    const mapRole = (serverRole, isMainProfile = false) => {
        switch (serverRole) {
            case "Admin":
                return "SuperAdmin";
            case "Client":
                return isMainProfile ? "Admin" : "Member";
            case "Validator":
                return "Validator";
            case "Modeller":
                return "Modeller";
            case "ModellerSupervisor":
                return "Modeller Supervisor";
            case "Guest":
                return "Guest";
            default:
                return "Member";
        }
    };

    // Imposta il saluto in base all'orario
    const currentHour = new Date().getHours();
    let greeting = "Good morning";
    if (currentHour >= 12 && currentHour < 18) {
        greeting = "Good afternoon";
    } else if (currentHour >= 18 && currentHour < 24) {
        greeting = "Good evening";
    } else if (currentHour >= 0 && currentHour < 6) {
        greeting = "Good night";
    }

    const dispatch = useDispatch();
    const collapsed = useSelector((state) => state.sidebar?.collapsed);

    // Ottieni role ed email da Redux o da secureGetItem
    const userPermissions = useSelector((state) => state.auth?.permissions);
    const role = userPermissions?.role || secureGetItem("role");
    const email = userPermissions?.email || secureGetItem("email");

    // Stato per controllare la visibilità di analytics-gateways
    const [analyticsGatewaysVisible, setAnalyticsGatewaysVisible] = useState(false);

    // Stato per tenere traccia di un singolo elemento attivo per tutti i menu
    const [activeMenuKey, setActiveMenuKey] = useState("home");

    // Reindirizza al login se mancano i dati sensibili
    useEffect(() => {
        if (!email || !role) {
            message.error("Sessione scaduta o dati mancanti. Effettua nuovamente l'accesso.");
            navigate("/login");
        }
    }, [email, role, navigate]);

    // Carica le notifiche tramite Redux al montaggio del componente
    useEffect(() => {
        if (email) {
            dispatch(fetchNotifications(email));
        }
    }, [email, dispatch]);

    // Recupera informazioni utente
    useEffect(() => {
        const fetchUserProfile = async () => {
            if (!email) return;
            try {
                const profileRef = doc(db, "Profiles", email);
                const profileSnap = await getDoc(profileRef);

                if (!profileSnap.exists()) return;

                const profileData = profileSnap.data();
                const serverRole = profileData.role || secureGetItem("role") || "";

                setFirstName(profileData.firstName || "");
                setLastName(profileData.lastName || "");

                // Recupera il nome dell'azienda e verifica se è mainProfile
                let companyName = "";
                let isMainProfile = false;

                if (profileData.clientRef) {
                    const clientDoc = await getDoc(doc(db, "Client", profileData.clientRef));
                    if (clientDoc.exists()) {
                        const clientData = clientDoc.data();
                        companyName = clientData.companyName || profileData.clientRef;

                        // Verifica se l'email corrente è il mainProfileRef
                        if (serverRole === "Client" && clientData.mainProfileRef === email) {
                            isMainProfile = true;
                        }
                    }
                }

                // Mappa il ruolo
                const mappedRoleValue = mapRole(serverRole, isMainProfile);

                setCompanyName(companyName);
                setMappedRole(mappedRoleValue);
            } catch (error) {
                console.error("Error fetching user profile:", error);
            }
        };

        fetchUserProfile();
    }, [email]);

    const handleLogout = async () => {
        const logoutMessageKey = "logoutMessage";
        try {
            await signOut(auth);
            message.success({
                content: "Logout successful",
                key: logoutMessageKey,
                duration: 2,
            });
            navigate("/login");
        } catch (error) {
            message.error({
                content: "Error logging out",
                key: logoutMessageKey,
                duration: 2,
            });
            console.error(error);
        }
    };

    useEffect(() => {
        const checkAnalyticsAccess = async () => {
            if (!email) return;
            try {
                // Recupera il documento Profile usando l'email
                const profileRef = doc(db, "Profiles", email.toLowerCase());
                const profileSnap = await getDoc(profileRef);

                if (!profileSnap.exists()) return;

                const profileData = profileSnap.data();
                const catalogsList = profileData.list_catalogues;

                if (!Array.isArray(catalogsList) || catalogsList.length === 0) return;

                // Query sui brand per trovare quelli con isGatewayCatalog: true
                const brandQuery = query(
                    collection(db, "Catalogues"),
                    where("__name__", "in", catalogsList) // Filtra i cataloghi presenti in catalogsList
                );

                const brandSnapshot = await getDocs(brandQuery);

                let found = false;
                brandSnapshot.forEach(doc => {
                    const brandData = doc.data();
                    if (brandData.isGatewayCatalog === true) {
                        found = true;
                    }
                });

                setAnalyticsGatewaysVisible(found);
            } catch (error) {
                console.error('[DEBUG] Errore nel controllo di Analytics Gateway:', error);
            }
        };

        checkAnalyticsAccess();
    }, [email]);

    // Stilizzazione personalizzata per il badge
    const CustomBadge = ({ count, type = "frame" }) => {

        if (!count || count <= 0) return null;

        // Determina la classe CSS in base allo stato del menu e al tipo
        const badgeClass = collapsed
            ? styles["badge-collapsed"]
            : (type === "frame" ? styles["badge-expanded-frame"] : styles["badge-expanded-order"]);

        return (
            <div
                className={badgeClass}
                style={{
                    backgroundColor: badgeBgColor,
                    color: badgeIconColor,
                }}
            >
                {count > 10 ? '10+' : count}
            </div>
        );
    };

    // Definizione dei menu in base al ruolo e alla presenza del servizio "ARShades Gateway"
    const menuItems = useMemo(() => {

        const items = [];

        // 1) Dashboard - sempre visibile per tutti
        items.push({
            key: "home",
            icon: <DashboardSharpIcon style={{ fontSize: iconSize, color: textColor }} />,
            label: <span className="menu-label-text" style={{ color: textColor }}>Dashboard</span>,
        });

        // Logica per ruoli specifici
        if (role?.toLowerCase() === "modeller") {
            // Dashboard, ARShades Library, My catalogues e Frame Validation per Modeller
            items.push({
                key: "ars-catalog",
                icon: <CustomSVGIcon src={ARShadesLibraryIcon} alt="ARShades Library" style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>ARShades Library</span>,
            });
            items.push({
                key: "my-catalogues",
                icon: <CustomSVGIcon src={ARShadesCataloguesIcon} alt="My catalogues" style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>My catalogues</span>,
            });
            items.push({
                key: "framevalidation",
                icon: (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                        <CheckCircleIcon style={{ fontSize: iconSize, color: textColor }} />
                        {collapsed && <CustomBadge count={frameValidationNotifCount} type="frame" />}
                    </div>
                ),
                label: (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                        <span className="menu-label-text" style={{ color: textColor }}>Frame Validation</span>
                        {!collapsed && <CustomBadge count={frameValidationNotifCount} type="frame" />}
                    </div>
                ),
            });
            return items;
        }

        if (role?.toLowerCase() === "modellersupervisor") {
            // Dashboard, ARShades Library, My catalogues e Frame Validation per ModelerSupervisor
            items.push({
                key: "ars-catalog",
                icon: <CustomSVGIcon src={ARShadesLibraryIcon} alt="ARShades Library" style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>ARShades Library</span>,
            });
            items.push({
                key: "my-catalogues",
                icon: <CustomSVGIcon src={ARShadesCataloguesIcon} alt="My catalogues" style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>My catalogues</span>,
            });
            items.push({
                key: "framevalidation",
                icon: (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                        <CheckCircleIcon style={{ fontSize: iconSize, color: textColor }} />
                        {collapsed && <CustomBadge count={frameValidationNotifCount} type="frame" />}
                    </div>
                ),
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <span className="menu-label-text" style={{ color: textColor }}>Frame Validation</span>
                        {!collapsed && <CustomBadge count={frameValidationNotifCount} type="frame" />}
                    </div>
                ),
            });
            return items;
        }

        // Menu completo per altri ruoli (Admin, Cliente, etc.)
        // 2) ARShades Library
        items.push({
            key: "ars-catalog",
            icon: <CustomSVGIcon src={ARShadesLibraryIcon} alt="ARShades Library" style={{ fontSize: iconSize, color: textColor }} />,
            label: <span className="menu-label-text" style={{ color: textColor }}>ARShades Library</span>,
        });

        // 3) My catalogues
        items.push({
            key: "my-catalogues",
            icon: <CustomSVGIcon src={ARShadesCataloguesIcon} alt="My catalogues" style={{ fontSize: iconSize, color: textColor }} />,
            label: <span className="menu-label-text" style={{ color: textColor }}>My catalogues</span>,
        });

        // 5) Analytics - disponibile per ruoli "Admin", "Cliente" e "Validator"
        if (role?.toLowerCase() === "admin" || role?.toLowerCase() === "cliente" || role?.toLowerCase() === "validator") {
            if (analyticsGatewaysVisible) {
                // Se sono presenti sia VTOs che Gateways, aggiungi il menu con sotto-menu
                items.push({
                    key: "analytics",
                    icon: <BarChartIcon style={{ fontSize: iconSize, color: textColor }} />,
                    label: <span className="menu-label-text" style={{ color: textColor }}>Analytics</span>,
                    children: [
                        // 5a) VTO services
                        { key: "analytics-vtos", label: <span className="menu-label-text" style={{ color: textColor }}>VTO services</span> },
                        // 5b) Gateways
                        { key: "analytics-gateways", label: <span className="menu-label-text" style={{ color: textColor }}>Gateways</span> },
                    ],
                });
            } else {
                // Se non ci sono Gateway, mostra comunque il menu Analytics con sotto-menu
                items.push({
                    key: "analytics",
                    icon: <BarChartIcon style={{ fontSize: iconSize, color: textColor }} />,
                    label: <span className="menu-label-text" style={{ color: textColor }}>Analytics</span>,
                    children: [
                        // 5a) VTO services
                        { key: "analytics-vtos", label: <span className="menu-label-text" style={{ color: textColor }}>VTO services</span> },
                        // 5b) Gateways - disabilitato
                        {
                            key: "analytics-gateways",
                            label: <span className="menu-label-text" style={{ color: textColor, opacity: 0.5 }}>Gateways</span>,
                            disabled: true
                        },
                    ],
                });
            }

            // 6) Subscriptions
            items.push({
                key: "subscriptions",
                icon: <CustomSVGIcon src={ARShadesSubscriptionsIcon} alt="Subscriptions" style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>Subscriptions</span>,
            });

            // 7) Orders (former Order 3D Assets) - con badge per le notifiche Order
            items.push({
                key: "orderpage",
                icon: (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                        <CustomSVGIcon src={ARShadesOrdersIcon} alt="Orders" style={{ fontSize: iconSize, color: textColor }} />
                        {collapsed && <CustomBadge count={ordersNotifCount} type="order" />}
                    </div>
                ),
                label: (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                        <span className="menu-label-text" style={{ color: textColor }}>Orders</span>
                        {!collapsed && <CustomBadge count={ordersNotifCount} type="order" />}
                    </div>
                ),
            });
        }

        // 8) Frame Validation - sempre visibile per tutti gli utenti con badge per le notifiche Variant
        items.push({
            key: "framevalidation",
            icon: (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <CheckCircleIcon style={{ fontSize: iconSize, color: textColor }} />
                    {collapsed && <CustomBadge count={frameValidationNotifCount} type="frame" />}
                </div>
            ),
            label: (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                    <span className="menu-label-text" style={{ color: textColor }}>Frame Validation</span>
                    {!collapsed && <CustomBadge count={frameValidationNotifCount} type="frame" />}
                </div>
            ),
        });

        // 9) Components (Admin only)
        if (role?.toLowerCase() === "admin") {
            items.push({
                key: "components",
                icon: <CodeIcon style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>Components</span>,
            });
        }

        // 10) Admin (Admin only)
        if (role?.toLowerCase() === "admin") {
            items.push({
                key: "admin",
                icon: <StorageIcon style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>Admin</span>,
            });
        }

        return items;
    }, [frameValidationNotifCount, ordersNotifCount, collapsed, role, analyticsGatewaysVisible, textColor, iconSize]);

    const bottomMenuItems = useMemo(
        () => [
            {
                key: "profile",
                icon: <SettingsIcon style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>Profile</span>,
            },
            {
                key: "logout",
                icon: <LogoutIcon style={{ fontSize: iconSize, color: textColor }} />,
                label: <span className="menu-label-text" style={{ color: textColor }}>Logout</span>,
                onClick: () => setIsLogoutModalVisible(true),
            },
        ],
        []
    );

    const handleMenuClick = ({ key }) => {
        if (isMobile) {
            dispatch(setSidebarState(true));
        }

        const findParentAndChild = (items, key) => {
            for (const item of items) {
                if (item.key === key) return { parentKey: item.key, childKey: null };
                if (item.children) {
                    const childItem = item.children.find((child) => child.key === key);
                    if (childItem) {
                        // Controlla se il child è disabilitato
                        if (childItem.disabled) {
                            return null; // Non navigare se disabilitato
                        }
                        return { parentKey: item.key, childKey: childItem.key };
                    }
                }
            }
            return null;
        };

        const selectedItem =
            findParentAndChild(menuItems, key) ||
            bottomMenuItems.find((item) => item.key === key);

        if (selectedItem) {
            if (selectedItem.onClick) {
                selectedItem.onClick();
                return;
            }

            // Impostiamo un unico elemento attivo
            setActiveMenuKey(key);
            setOpenKeys([selectedItem.parentKey || key]);

            const path = selectedItem.path || `/${key}`;
            navigate(path);
        }
    };

    // Aggiorniamo l'elemento attivo in base all'URL corrente
    useEffect(() => {
        const path = location.pathname === "/" ? "/home" : location.pathname;

        // Definisci le relazioni parent-child per i menu
        const getMenuKeyFromPath = (pathname) => {
            // ARSCatalog parent-child relationships
            if (pathname.startsWith('/ars-catalog')) {
                return 'ars-catalog';
            }

            // My Catalogues parent-child relationships
            if (pathname.startsWith('/my-catalogues')) {
                return 'my-catalogues';
            }

            // Analytics parent-child relationships
            if (pathname === '/analytics' || pathname === '/analytics-vtos') {
                return 'analytics-vtos';
            }
            if (pathname === '/analytics-gateways') {
                return 'analytics-gateways';
            }

            // Frame Validation parent-child relationships
            if (pathname.startsWith('/framevalidation')) {
                return 'framevalidation';
            }

            // Per tutti gli altri percorsi, usa il primo segmento dopo lo slash
            const segments = pathname.split('/').filter(Boolean);
            return segments[0] || 'home';
        };

        const menuKey = getMenuKeyFromPath(path);
        setActiveMenuKey(menuKey);

        const findParentAndChild = (items, path) => {
            for (const item of items) {
                if (`/${item.key}` === path) return { parentKey: item, childKey: null };
                if (item.children) {
                    const childItem = item.children.find(
                        (child) => `/${child.key}` === path
                    );
                    if (childItem) return { parentKey: item, childKey: childItem };
                }
            }
            return null;
        };

        const selectedItem = findParentAndChild(menuItems, path);

        if (selectedItem) {
            setOpenKeys([selectedItem.parentKey.key]);
        }

        if (location.pathname === "/") {
            navigate("/home");
        }

        localStorage.setItem("lastVisitedPage", location.pathname);
    }, [location, menuItems, bottomMenuItems, navigate]);

    // Determina se è un dispositivo mobile
    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

    // Gestisce il collasso automatico in base alla dimensione dello schermo
    useEffect(() => {
        if (isMobile) {
            dispatch(setSidebarState(true)); // Collassa la sidebar
            dispatch(setSidebarBroken(true)); // Imposta broken a true
        } else {
            dispatch(setSidebarBroken(false)); // Imposta broken a false
        }
    }, [isMobile, dispatch]);

    const handleMouseEnter = () => {
        if (!isMobile) {
            dispatch(setSidebarState(false));
        }
    };

    const handleMouseLeave = () => {
        if (!isMobile) {
            dispatch(setSidebarState(true));
        }
    };

    return (
        <Layout style={{ height: "100vh", width: "100vw", overflow: "hidden" }}>
            {/* Header spostato sopra la sidebar */}
            <Header
                style={{
                    width: "100%",
                    background: backgroundColor,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0 clamp(0.5rem, 2vw, 1rem)",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1001,
                    height: "clamp(4rem, 6vw, 5rem)",
                    transition: "all 0.4s ease",
                }}
            >
                {/* Logo spostato nell'header a sinistra */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    height: "clamp(4rem, 6vw, 5rem)"
                }}>
                    <img
                        src={logo}
                        alt="Logo"
                        style={{
                            width: "clamp(3rem, 4vw, 3.75rem)",
                            height: "clamp(3rem, 4vw, 3.75rem)",
                            objectFit: "contain"
                        }}
                    />
                </div>

                <div style={{
                    pointerEvents: "none",
                    marginLeft: "clamp(0.5rem, 1vw, 1rem)"
                }}>
                    <h1 style={{
                        margin: 0,
                        fontSize: "clamp(1rem, 3vw, 1.5rem)",
                        fontWeight: "700",
                        letterSpacing: "0.063rem",
                        color: textColor,
                        textTransform: "uppercase"
                    }}>
                        ARShades Studio
                    </h1>
                </div>

                <div style={{ marginLeft: "auto", display: "flex", gap: "clamp(0.75rem, 2vw, 1.5rem)", alignItems: "center" }}>
                    {/* Informazioni utente - nascoste quando si è nella Home */}
                    {activeMenuKey !== "home" && (
                        <div style={{ textAlign: "right", lineHeight: "1.2" }}>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: "clamp(0.75rem, 1vw, 1rem)",
                                    color: headerTextColor,
                                }}
                            >
                                {greeting},{" "}
                                <span style={{ fontWeight: "bold", color: headerTextColor }}>
                                    {firstName} {lastName}
                                </span>
                            </p>
                            <p
                                style={{
                                    fontSize: "clamp(0.75rem, 1vw, 1rem)",
                                    color: headerTextColor,
                                    marginTop: "0.188rem",
                                    marginBottom: 0,
                                }}
                            >
                                You are{" "}
                                <span style={{ fontWeight: "bold", color: headerTextColor }}>
                                    {mappedRole === "SuperAdmin" ? "Super Admin" : mappedRole}
                                </span>{" "}
                                for{" "}
                                <span style={{ fontWeight: "bold", color: headerTextColor }}>
                                    {mappedRole === "SuperAdmin" || mappedRole === "Modeller" || mappedRole === "Modeller Supervisor" ? "Spaarkly" : companyName}
                                </span>
                            </p>
                        </div>
                    )}

                    {/* Pulsante di aiuto */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            width: "clamp(1.5rem, 2vw, 1.875rem)",
                            height: "clamp(1.5rem, 2vw, 1.875rem)",
                            backgroundColor: badgeBackgroundColor,
                            borderRadius: "0.25rem",
                            transition: 'background-color 0.3s ease'
                        }}
                        onClick={() => setIsSupportModalVisible(true)}
                    >
                        <HelpOutlineIcon
                            style={{
                                fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
                                color: badgeIconColor
                            }}
                        />
                    </div>

                    {/* Dropdown delle notifiche */}
                    <NotificationsDropdown
                        badgeBackgroundColor={badgeBackgroundColor}
                        badgeIconColor={badgeIconColor}
                        onNotificationCountChange={(count) => {
                            // Ricarica i conteggi categorizzati quando le notifiche cambiano
                            if (email) {
                                dispatch(fetchNotifications(email));
                            }
                        }}
                    />
                </div>
            </Header>

            {/* Sidebar spostata sotto l'header */}
            <motion.div
                className={styles["sidebar-animated"]}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                animate={{
                    width: collapsed ? "clamp(4rem, 6vw, 5rem)" : "clamp(20rem, 25vw, 23rem)"
                }}
                transition={{
                    duration: 0.3,
                    ease: [0.4, 0, 0.2, 1]
                }}
                style={{
                    "--background-color-active": backgroundColorActive,
                    "--background-color-active-hover": backgroundColorActiveHover,
                    "--text-active-color": textActiveColor,
                    top: "clamp(4rem, 6vw, 5rem)",
                    height: `calc(100% - clamp(4rem, 6vw, 5rem))`
                }}
            >
                <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    {/* Menu principale */}
                    <div className={styles["custom-menu"]} style={{ flex: 1 }}>
                        {menuItems.map((item) => {
                            if (item.children) {
                                // È un submenu
                                const isSubMenuOpen = openKeys.includes(item.key);
                                const isItemActive = item.key === activeMenuKey;

                                return (
                                    <div key={item.key}>
                                        <div
                                            className={`${styles["menu-item"]} ${styles["submenu-title"]} ${isSubMenuOpen ? styles["submenu-open"] : ""} ${isItemActive ? styles["menu-item-active"] : ""}`}
                                            onClick={() => {
                                                const newOpenKeys = isSubMenuOpen
                                                    ? openKeys.filter(key => key !== item.key)
                                                    : [...openKeys, item.key];
                                                setOpenKeys(newOpenKeys);
                                            }}
                                        >
                                            <div className={styles["icon-container"]}>
                                                {React.isValidElement(item.icon) && item.icon.type === 'div' ?
                                                    React.cloneElement(item.icon, {
                                                        children: React.Children.map(item.icon.props.children, (child, index) => {
                                                            if (index === 0 && React.isValidElement(child)) {
                                                                // Primo figlio è l'icona
                                                                return React.cloneElement(child, {
                                                                    style: {
                                                                        ...child.props.style,
                                                                        color: isItemActive ? textActiveColor : textColor,
                                                                        fontSize: iconSize
                                                                    }
                                                                });
                                                            }
                                                            return child; // Badge rimane invariato
                                                        })
                                                    }) :
                                                    React.cloneElement(item.icon, {
                                                        style: {
                                                            ...item.icon.props.style,
                                                            color: isItemActive ? textActiveColor : textColor,
                                                            fontSize: iconSize
                                                        }
                                                    })
                                                }
                                            </div>
                                            <AnimatePresence mode="wait">
                                                {!collapsed && (
                                                    <motion.div
                                                        className={styles["menu-item-label"]}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: -20 }}
                                                        transition={{
                                                            duration: 0.2,
                                                            delay: 0.1,
                                                            ease: [0.4, 0, 0.2, 1]
                                                        }}
                                                    >
                                                        {typeof item.label === 'string'
                                                            ? item.label
                                                            : React.cloneElement(item.label, {
                                                                style: {
                                                                    ...item.label.props.style,
                                                                    color: isItemActive ? textActiveColor : textColor
                                                                }
                                                            })
                                                        }
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                            <AnimatePresence mode="wait">
                                                {!collapsed && (
                                                    <motion.div
                                                        className={`${styles["submenu-arrow"]} ${isSubMenuOpen ? styles["submenu-arrow-open"] : ""}`}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: -10 }}
                                                        transition={{
                                                            duration: 0.2,
                                                            delay: 0.15,
                                                            ease: [0.4, 0, 0.2, 1]
                                                        }}
                                                    >
                                                        <div className={styles["arrow"]} style={{ color: isItemActive ? textActiveColor : textColor }}></div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        <AnimatePresence mode="wait">
                                            {!collapsed && isSubMenuOpen && (
                                                <motion.div
                                                    className={styles["submenu-items"]}
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{
                                                        duration: 0.2,
                                                        delay: 0.1,
                                                        ease: [0.4, 0, 0.2, 1]
                                                    }}
                                                >
                                                    {item.children.map((child) => {
                                                        const isChildActive = child.key === activeMenuKey;
                                                        const isDisabled = child.disabled;
                                                        return (
                                                            <div
                                                                key={child.key}
                                                                className={`${styles["submenu-item"]} ${isChildActive ? styles["menu-item-active"] : ""} ${isDisabled ? styles["menu-item-disabled"] : ""}`}
                                                                onClick={() => !isDisabled && handleMenuClick({ key: child.key })}
                                                                style={{
                                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                    opacity: isDisabled ? 0.5 : 1
                                                                }}
                                                            >
                                                                <div
                                                                    className={styles["submenu-item-content"]}
                                                                    style={{
                                                                        color: isChildActive ? textActiveColor : textColor
                                                                    }}
                                                                >
                                                                    {typeof child.label === 'string'
                                                                        ? child.label
                                                                        : React.cloneElement(child.label, {
                                                                            style: {
                                                                                ...child.label.props.style,
                                                                                color: isChildActive ? textActiveColor : textColor
                                                                            }
                                                                        })
                                                                    }
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            }

                            // Item normale
                            const isItemActive = item.key === activeMenuKey;

                            return (
                                <div
                                    key={item.key}
                                    className={`${styles["menu-item"]} ${isItemActive ? styles["menu-item-active"] : ""}`}
                                    onClick={() => handleMenuClick({ key: item.key })}
                                >
                                    <div className={styles["icon-container"]}>
                                        {React.isValidElement(item.icon) && item.icon.type === 'div' ?
                                            React.cloneElement(item.icon, {
                                                children: React.Children.map(item.icon.props.children, (child, index) => {
                                                    if (index === 0 && React.isValidElement(child)) {
                                                        // Primo figlio è l'icona
                                                        return React.cloneElement(child, {
                                                            style: {
                                                                ...child.props.style,
                                                                color: isItemActive ? textActiveColor : textColor,
                                                                fontSize: iconSize
                                                            }
                                                        });
                                                    }
                                                    return child; // Badge rimane invariato
                                                })
                                            }) :
                                            React.cloneElement(item.icon, {
                                                style: {
                                                    ...item.icon.props.style,
                                                    color: isItemActive ? textActiveColor : textColor,
                                                    fontSize: iconSize
                                                }
                                            })
                                        }
                                    </div>
                                    <AnimatePresence mode="wait">
                                        {!collapsed && (
                                            <motion.div
                                                className={styles["menu-item-label"]}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -20 }}
                                                transition={{
                                                    duration: 0.2,
                                                    delay: 0.1,
                                                    ease: [0.4, 0, 0.2, 1]
                                                }}
                                                style={{
                                                    color: isItemActive ? textActiveColor : textColor
                                                }}
                                            >
                                                {typeof item.label === 'string'
                                                    ? item.label
                                                    : React.cloneElement(item.label, {
                                                        style: {
                                                            ...item.label.props.style,
                                                            color: isItemActive ? textActiveColor : textColor
                                                        }
                                                    })
                                                }
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>

                    {/* Menu inferiore */}
                    <div className={styles["custom-menu"]} style={{ marginBottom: "clamp(1rem, 2vw, 1.5rem)" }}>
                        {bottomMenuItems.map((item) => {
                            const isItemActive = item.key === activeMenuKey;
                            return (
                                <div
                                    key={item.key}
                                    className={`${styles["menu-item"]} ${isItemActive ? styles["menu-item-active"] : ""}`}
                                    onClick={() => item.onClick ? item.onClick() : handleMenuClick({ key: item.key })}
                                >
                                    <div className={styles["icon-container"]}>
                                        {React.cloneElement(item.icon, {
                                            style: {
                                                ...item.icon.props.style,
                                                color: isItemActive ? textActiveColor : textColor,
                                                fontSize: iconSize
                                            }
                                        })}
                                    </div>
                                    <AnimatePresence mode="wait">
                                        {!collapsed && (
                                            <motion.div
                                                className={styles["menu-item-label"]}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -20 }}
                                                transition={{
                                                    duration: 0.2,
                                                    delay: 0.1,
                                                    ease: [0.4, 0, 0.2, 1]
                                                }}
                                                style={{
                                                    color: isItemActive ? textActiveColor : textColor
                                                }}
                                            >
                                                {typeof item.label === 'string'
                                                    ? item.label
                                                    : React.cloneElement(item.label, {
                                                        style: {
                                                            ...item.label.props.style,
                                                            color: isItemActive ? textActiveColor : textColor
                                                        }
                                                    })
                                                }
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </motion.div>
            <Layout style={{
                width: "100%",
                height: "100%"
            }}>
                <Content
                    id="page-content-wrapper" // Aggiungo l'ID qui
                    style={{
                        marginLeft: "5rem",
                        marginRight: "0",
                        marginTop: "clamp(4rem, 6vw, 5rem)",
                        marginBottom: "0",
                        padding: "clamp(1rem, 2vw, 1.5rem)",
                        minHeight: "17.5rem",
                        height: `calc(100vh - clamp(4rem, 6vw, 5rem))`,
                        overflowY: "auto"
                    }}
                >
                    <Outlet />
                </Content>
            </Layout>
            {/* Modale di conferma Logout */}
            <Modal
                title="Confirm Logout"
                open={isLogoutModalVisible}
                onOk={() => {
                    setIsLogoutModalVisible(false);
                    handleLogout();
                }}
                onCancel={() => setIsLogoutModalVisible(false)}
                okText="Logout"
                cancelText="Cancel"
                centered
            >
                <p>Are you sure you want to logout?</p>
            </Modal>

            {/* Modale di supporto */}
            <SupportModal
                visible={isSupportModalVisible}
                onClose={() => setIsSupportModalVisible(false)}
            />
        </Layout>
    );
};

export default DashboardLayout;